import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Select } from '../../components/ui/select'
import { api, get } from '../../lib/api'
import type { BoothAssignment } from './types'
import { AButton, Chip, Note, Section } from './Section'

interface BoothAssignmentData {
  assignments: BoothAssignment[]
  schools: string[]
}

export function BoothsSection() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<BoothAssignment[]>([])
  const [error, setError] = useState<string | null>(null)

  const configuration = useQuery({
    queryKey: ['admin-booth-assignments'],
    queryFn: () => get<BoothAssignmentData>('/api/admin/booth-assignments', 'staff'),
  })

  useEffect(() => {
    if (configuration.data) setDraft(configuration.data.assignments)
  }, [configuration.data])

  const save = useMutation({
    mutationFn: (assignments: BoothAssignment[]) =>
      api<BoothAssignmentData>('/api/admin/booth-assignments', {
        method: 'PUT',
        body: JSON.stringify({ assignments }),
        auth: 'staff',
      }),
    onSuccess: (result) => {
      setDraft(result.assignments)
      setError(null)
      queryClient.setQueryData<BoothAssignmentData>(['admin-booth-assignments'], (current) =>
        current ? { ...current, assignments: result.assignments } : current,
      )
      queryClient.invalidateQueries({ queryKey: ['admin-booths'] })
      queryClient.invalidateQueries({ queryKey: ['admin-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['admin-recent'] })
    },
    onError: () => setError('Could not save the booth assignments. Refresh and try again.'),
  })

  const schools = configuration.data?.schools ?? []
  const assignedSchools = draft.map((assignment) => assignment.school)
  const hasDuplicates = new Set(assignedSchools).size !== assignedSchools.length
  const changed = JSON.stringify(draft) !== JSON.stringify(configuration.data?.assignments ?? [])

  if (configuration.isPending) {
    return (
      <Section title="Booth map" description="Assign each faculty to its physical booth number.">
        <div className="grid place-items-center gap-3 py-24">
          <span className="size-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
          <p className="text-sm text-slate-500">Loading booth assignments…</p>
        </div>
      </Section>
    )
  }

  if (configuration.isError || !configuration.data) {
    return (
      <Section title="Booth map" description="Assign each faculty to its physical booth number.">
        <Note tone="error">Could not load booth assignments. Refresh this section to try again.</Note>
      </Section>
    )
  }

  return (
    <Section
      title="Booth map"
      description={`${draft.length} booths · each faculty can be assigned once`}
      actions={
        <>
          {changed && <Chip tone="attention">Unsaved changes</Chip>}
          <AButton
            tone="outline"
            size="sm"
            disabled={!changed || save.isPending}
            onClick={() => {
              setDraft(configuration.data.assignments)
              setError(null)
            }}
          >
            <RotateCcw className="size-3.5" strokeWidth={2.2} />
            Discard
          </AButton>
          <AButton
            size="sm"
            disabled={!changed || hasDuplicates || save.isPending}
            onClick={() => {
              setError(null)
              save.mutate(draft)
            }}
          >
            <Check className="size-3.5" strokeWidth={2.2} />
            {save.isPending ? 'Saving…' : 'Save assignments'}
          </AButton>
        </>
      }
    >
      <div className="space-y-3">
        <Note>
          Booth numbers stay fixed. Booth manager accounts remain assigned to their number;
          changing a faculty here also updates its label on existing scan history and judging records.
        </Note>

        {error && <Note tone="error">{error}</Note>}

        {hasDuplicates && (
          <Note tone="attention">Assign a different faculty to every booth before saving.</Note>
        )}

        {/*
          A form, not a data table, so it stacks rather than scrolling
          sideways: the booth number sits above its picker on a phone and
          beside it from `sm` up. The select was previously inside a
          520px-minimum table, which pushed it off a 390px screen.
        */}
        <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-white bg-white/95 shadow-lg shadow-blue-900/5">
          <p className="hidden bg-slate-50/80 px-4 py-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400 sm:block">
            Booth number &amp; assigned faculty
          </p>

          {draft.map((assignment) => (
            <div
              key={assignment.boothNo}
              className="gap-2 px-4 py-3 sm:grid sm:grid-cols-[8rem_1fr] sm:items-center sm:gap-4 sm:py-2.5"
            >
              <label
                htmlFor={`booth-school-${assignment.boothNo}`}
                className="mb-1.5 block font-bold tabular-nums text-[#0B1E3F] sm:mb-0"
              >
                Booth {assignment.boothNo}
              </label>
              <Select
                id={`booth-school-${assignment.boothNo}`}
                value={assignment.school}
                options={schools}
                onValueChange={(school) => {
                  setDraft((current) =>
                    current.map((entry) =>
                      entry.boothNo === assignment.boothNo ? { ...entry, school } : entry,
                    ),
                  )
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
